import { useState } from "react";
import { Loader2, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { API } from "../lib/api";
import { track } from "../lib/analytics";
import { fmtLong } from "../lib/calendarUtils";

const fmtMoney = (n) => (typeof n === "number" ? `$${n.toFixed(2)}` : "—");

const EMPTY = {
  parent_first: "",
  parent_last: "",
  email: "",
  phone: "",
  child_first: "",
  child_last: "",
  dob: "",
  notes: "",
  agree: false,
  website: "",
};

export default function BookingDialog({ ctx, onClose, onFilled }) {
  const schedule = ctx?.schedule || null;
  const option = ctx?.option || null;
  const sessionDate = ctx?.sessionDate || null;
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e,
    }));

  const close = () => {
    if (submitting) return;
    setForm(EMPTY);
    setError("");
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    if (!form.agree) {
      setError("Please agree to the enrollment policies to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/site/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          class_schedule_id: schedule.id,
          ...(option?.option_id ? { payment_option_id: option.option_id } : {}),
          ...(sessionDate ? { session_date: sessionDate } : {}),
          parent: {
            first_name: form.parent_first.trim(),
            last_name: form.parent_last.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
          },
          child: {
            first_name: form.child_first.trim(),
            last_name: form.child_last.trim(),
            ...(form.dob ? { date_of_birth: form.dob } : {}),
            ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
          },
          agree_policies: form.agree,
          website: form.website,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.checkout_url) {
        try {
          sessionStorage.setItem("nexora_enrollment_id", data.enrollment_id || "");
          sessionStorage.setItem("nexora_booking_title", schedule.title || "");
        } catch {
          /* sessionStorage unavailable — success page falls back gracefully */
        }
        track("booking_checkout_redirect", { schedule_id: schedule.id });
        window.location.assign(data.checkout_url);
        return;
      }

      if (res.status === 409) {
        setError(typeof data.detail === "string" ? data.detail : "Sorry — this class just filled up.");
        setTimeout(() => onFilled?.(), 1800);
      } else if (res.status === 403) {
        setError(typeof data.detail === "string" ? data.detail : "This booking option is not available.");
      } else if (res.status === 422) {
        const d = data.detail;
        if (Array.isArray(d)) {
          setError(
            d
              .map((x) => {
                const field = (x.loc || []).slice(-1)[0];
                return field ? `${String(field).replace(/_/g, " ")}: ${x.msg}` : x.msg;
              })
              .join(" · ")
          );
        } else {
          setError(typeof d === "string" ? d : "Please check the form fields and try again.");
        }
      } else if (res.status === 404) {
        setError("This class is no longer available.");
        setTimeout(() => onFilled?.(), 1800);
      } else if (res.status === 429) {
        setError(data.detail || "Too many attempts — please wait a few minutes and try again.");
      } else if (res.status === 400) {
        setError(typeof data.detail === "string" ? data.detail : "Invalid booking selection — please try again.");
      } else {
        setError(
          typeof data.detail === "string"
            ? data.detail
            : "Something went wrong starting payment — please try again."
        );
      }
    } catch {
      setError("Something went wrong — please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!schedule) return null;
  const p = (option ? option.pricing : schedule.pricing) || {};
  const isSub = option ? !!option.recurring : schedule.billing_type === "subscription";
  const optionLabel = option?.label || (isSub ? "Monthly Enrollment" : "");

  const input =
    "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A6B2]/40 focus:border-[#00A6B2]";

  return (
    <Dialog open={!!schedule} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto rounded-[1.5rem]"
        data-testid="booking-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold" style={{ color: "#123B6D" }}>
            {isSub ? "Enroll" : "Book"}: {schedule.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            {optionLabel ? `${optionLabel} · ` : ""}
            {sessionDate ? `Class date: ${fmtLong(sessionDate)} · ` : ""}
            {fmtMoney(p.total_due_today)} due today
            {isSub ? ` · then ${fmtMoney(p.monthly_amount)} per month, billed monthly until cancelled` : " · one-time payment"}
            . Includes tuition {fmtMoney(p.base_price)}, booking fee {fmtMoney(p.booking_fee_amount)}, GST {fmtMoney(p.tax_amount)}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid gap-3 mt-1">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Parent first name *" value={form.parent_first} onChange={set("parent_first")} maxLength={80} className={input} data-testid="booking-parent-first" />
            <input required placeholder="Parent last name *" value={form.parent_last} onChange={set("parent_last")} maxLength={80} className={input} data-testid="booking-parent-last" />
          </div>
          <input required type="email" placeholder="Parent email *" value={form.email} onChange={set("email")} className={input} data-testid="booking-email" />
          <input required placeholder="Parent phone *" value={form.phone} onChange={set("phone")} minLength={5} maxLength={32} className={input} data-testid="booking-phone" />
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Child first name *" value={form.child_first} onChange={set("child_first")} maxLength={80} className={input} data-testid="booking-child-first" />
            <input required placeholder="Child last name *" value={form.child_last} onChange={set("child_last")} maxLength={80} className={input} data-testid="booking-child-last" />
          </div>
          <label className="grid gap-1 text-xs font-semibold text-slate-600">
            Child date of birth (optional)
            <input type="date" value={form.dob} onChange={set("dob")} className={input} data-testid="booking-dob" />
          </label>
          <textarea
            placeholder="Notes / anything we should know (optional)"
            value={form.notes}
            onChange={set("notes")}
            maxLength={1000}
            rows={3}
            className={input}
            data-testid="booking-notes"
          />
          {/* Honeypot — hidden from real users */}
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={set("website")}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
          />
          <label className="flex items-start gap-2.5 text-xs text-slate-600 leading-relaxed">
            <input
              type="checkbox"
              checked={form.agree}
              onChange={set("agree")}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#FF8C42]"
              data-testid="booking-agree-checkbox"
            />
            <span>
              I agree to Nexora's enrollment policies
              {isSub ? " and understand this is a monthly subscription billed until cancelled" : ""}
              . Payment is processed securely by Stripe.
            </span>
          </label>

          {error && (
            <p
              className="rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5 text-xs font-semibold text-red-700"
              role="alert"
              data-testid="booking-error"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="booking-submit-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: "#FF8C42" }}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Starting secure checkout…
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" /> Continue to secure payment
              </>
            )}
          </button>
          <p className="text-[11px] text-slate-400 text-center -mt-1">
            You'll be redirected to Stripe to complete payment. No card details are stored on our site.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
